import {
  scrubObservability,
  type ObservabilityPolicy,
  type ObservabilityRule,
} from "@alchema/shared";

const booleanRule = { kind: "boolean" } as const satisfies ObservabilityRule;
const countRule = { kind: "number", integer: true, min: 0, max: 10_000_000 } as const satisfies ObservabilityRule;
const durationRule = { kind: "number", min: 0, max: 86_400_000 } as const satisfies ObservabilityRule;
const scoreRule = { kind: "number", min: 0, max: 100 } as const satisfies ObservabilityRule;
const statusCodeRule = { kind: "number", integer: true, min: 100, max: 599 } as const satisfies ObservabilityRule;
const digestRule = { kind: "digest", version: "op1" } as const satisfies ObservabilityRule;

function enumRule<const T extends readonly (string | number | boolean)[]>(values: T): ObservabilityRule {
  return { kind: "enum", values };
}

const localeRule = enumRule(["en", "de", "fr", "es", "it", "nl", "pl", "fi", "da", "sv"]);
const tierRule = enumRule(["free", "student", "pro", "premium", "unknown"]);
const modelVersionRule = enumRule(["v1_medium", "v2_small", "v1_large", "unknown"]);
const modelIdRule = enumRule(["mistral-small-latest", "mistral-medium-latest", "mistral-large-latest"]);
const modelTierRule = enumRule(["data_processing", "user_facing", "routing"]);
const agentRule = enumRule([
  "scraper", "normalizer", "resume-tailor", "career-coach", "market-intel", "recommender", "analytics",
  "job-parser", "skills-matcher", "ats-critic", "career-router", "mock-interviewer", "profile-analyzer",
  "career-path-planner", "negotiation-coach", "company-researcher", "feedback-analyzer", "action-recommender",
  "question-generator", "unknown",
]);
const actionRule = enumRule([
  "resume-parse", "resume-tailor", "cover-letter", "salary-analysis", "interview-prep", "job-search",
  "bullet-rewrite", "summary-generation", "skill-suggestion", "salary-benchmark", "salary-compare",
  "negotiation-script", "qa-answer", "story-generation", "deep-match", "pdf_export", "pdf_export_fallback",
  "markdown_export", "candidate_ai_notice", "student_verification", "low_credit_banner", "job_tracking_limit",
  "human_review_preference", "ai-tailor", "cover-letter-generated", "ai_action", "credit_exhaustion_wait",
  "job-extraction", "email-classify", "company-brief", "market-intel",
  "resume.generateBullets", "resume.generateSummary", "resume.generateSkillDescription", "resume.parseUpload",
  "job.analyze", "job.tailor", "coverLetter.generate", "coverLetter.regenerate", "salary.benchmark",
  "salary.compare", "salary.negotiate", "interview.generateForJob", "interview.submitAnswer",
  "interview.getCompanyBrief", "interview.questionBank",
]);
const statusRule = enumRule(["ok", "success", "failed", "fallback", "timeout", "rejected", "completed", "unknown"]);
const errorTypeRule = enumRule(["runtime", "network", "not_found", "server", "provider", "validation", "unknown", "error", "type_error", "range_error", "syntax_error", "unknown_error"]);
const errorCodeRule = enumRule([
  "unknown", "timeout", "network", "rate_limited", "upstream_rejected", "validation", "parse_failed",
  "provider_failed", "gateway_failed", "circuit_open", "retry_exhausted", "configuration_missing",
]);

const LOG_MESSAGE_CODES: Readonly<Record<string, string>> = {
  "AI Deep Match failed, falling back to deterministic": "ai_deep_match_fallback",
  "AI keyword injection failed": "ai_keyword_injection_failed",
  "ATS critic early exit: deterministic pre-check passed": "ats_critic_early_exit",
  "Career coaching swarm failed": "career_coaching_swarm_failed",
  "Career coaching: low confidence, defaulting to interview-questions": "career_coaching_low_confidence",
  "Career coaching: router failed, defaulting to interview-questions": "career_coaching_router_fallback",
  "Career coaching: skipping router for multi-turn": "career_coaching_router_skipped",
  "Career coaching: specialist failed, falling back to career-coach": "career_coaching_specialist_fallback",
  "Company brief: circuit breaker open, using fallback": "company_brief_circuit_open",
  "Convergence: diminishing returns, stopping early": "convergence_diminishing_returns",
  "Convergence: oscillation detected, stopping early": "convergence_oscillation",
  "Document processing failed": "document_processing_failed",
  "Document processing in progress": "document_processing_pending",
  "EU_AI_ACT_ANNEX_III_AUDIT": "annex_iii_audit",
  "Error polling document status": "document_poll_failed",
  "Failed to analyze keywords": "keyword_analysis_failed",
  "Failed to delete document": "document_delete_failed",
  "Failed to generate company brief": "company_brief_failed",
  "Failed to generate cover letter": "cover_letter_failed",
  "Cover letter generation failed": "cover_letter_generation_failed",
  "Failed to generate interview feedback": "interview_feedback_failed",
  "Failed to generate interview questions": "interview_questions_failed",
  "Failed to generate negotiation script": "negotiation_script_failed",
  "Failed to generate summary": "summary_generation_failed",
  "Failed to get/create shared library": "shared_library_failed",
  "Failed to get/create user library": "user_library_failed",
  "Failed to list documents": "document_list_failed",
  "Failed to rewrite bullets": "bullet_rewrite_failed",
  "Failed to suggest keywords": "keyword_suggestion_failed",
  "Failed to upload document": "document_upload_failed",
  "Interview feedback: circuit breaker open, using fallback": "interview_feedback_circuit_open",
  "Interview questions: circuit breaker open, using fallback": "interview_questions_circuit_open",
  "Negotiation script: circuit breaker open, using fallback": "negotiation_script_circuit_open",
  "Normalizer ESCO lookup failed": "normalizer_esco_lookup_failed",
  "Normalizer agent failed": "normalizer_agent_failed",
  "Agent path failed, using legacy fallback": "agent_legacy_fallback",
  "Market intel salary query failed": "market_intel_salary_query_failed",
  "Market intel skills query failed": "market_intel_skills_query_failed",
  "Analytics materialized view query failed": "analytics_view_query_failed",
  "Analytics agent failed": "analytics_agent_failed",
  "Recommender job search failed": "recommender_job_search_failed",
  "Prompt sanitizer neutralized untrusted content": "prompt_sanitizer_neutralized",
  "Qdrant gateway index error": "qdrant_index_error",
  "Qdrant gateway index failed": "qdrant_index_failed",
  "Qdrant gateway read error": "qdrant_read_error",
  "Qdrant gateway search error": "qdrant_search_error",
  "Qdrant gateway search failed": "qdrant_search_failed",
  "Resume extraction failed": "resume_extraction_failed",
  "Resume response parse failed": "resume_response_parse_failed",
  "Resume AI extraction failed": "resume_ai_extraction_failed",
  "Resume tailoring iteration completed": "resume_tailoring_iteration_completed",
  "Resume tailoring swarm completed": "resume_tailoring_swarm_completed",
  "Resume tailoring swarm failed": "resume_tailoring_swarm_failed",
  "Story generation failed": "story_generation_failed",
  "Timeout waiting for document processing": "document_processing_timeout",
  "appendConversation failed": "conversation_append_failed",
  "deep-match: candidate scoring audit write failed": "deep_match_audit_failed",
  "prompt-injection neutralized in deep-match input": "deep_match_prompt_neutralized",
  "prompt-injection neutralized in resume-tailoring swarm input": "resume_tailoring_prompt_neutralized",
  "runScraperAgent failed": "scraper_agent_failed",
  "runScraperPipeline failed": "scraper_pipeline_failed",
  "startConversation failed": "conversation_start_failed",
};

export function safeLogMessage(message: unknown): string {
  return typeof message === "string" ? (LOG_MESSAGE_CODES[message] ?? "observability_event") : "observability_event";
}

export const LOGGER_CONTEXT_POLICY: ObservabilityPolicy = {
  fields: {
    action: actionRule,
    agent_name: agentRule,
    agentName: { ...agentRule, outputKey: "agent_name" },
    count: countRule,
    duration_ms: durationRule,
    durationMs: { ...durationRule, outputKey: "duration_ms" },
    error: { kind: "error" },
    error_code: errorCodeRule,
    error_type: errorTypeRule,
    fallback: booleanRule,
    has_company: booleanRule,
    has_job_title: booleanRule,
    model_tier: modelTierRule,
    model_version: modelVersionRule,
    neutralized: booleanRule,
    neutralizedCount: { ...countRule, outputKey: "neutralized_count" },
    operation: enumRule(["search", "read", "index", "embed", "query", "upload", "delete", "list", "poll"]),
    retries_exhausted: booleanRule,
    score: scoreRule,
    status: statusRule,
    status_code: statusCodeRule,
    success: booleanRule,
    timeout: booleanRule,
    token_count: countRule,
  },
};

export function scrubLoggerContext(context: unknown): Record<string, unknown> {
  return scrubObservability(context, LOGGER_CONTEXT_POLICY);
}

const baseAiFields: ObservabilityPolicy["fields"] = {
  action: actionRule,
  duration_ms: durationRule,
  has_company: booleanRule,
  has_job_title: booleanRule,
  is_ai: booleanRule,
  match_score: scoreRule,
  overall_score: scoreRule,
  retries_exhausted: booleanRule,
  stories_count: countRule,
  success: booleanRule,
};

export const OBSERVABILITY_EVENT_POLICIES: Readonly<Record<string, ObservabilityPolicy>> = {
  user_signed_up: { fields: { method: enumRule(["email", "google"]), locale: localeRule } },
  onboarding_completed: { fields: { has_resume: booleanRule, steps_completed: countRule } },
  resume_uploaded: { fields: { file_type: enumRule(["pdf", "docx", "txt"]), file_size_kb: countRule, status: statusRule } },
  job_searched: { fields: { result_count: countRule, has_query: booleanRule, has_filters: booleanRule } },
  job_viewed: { fields: { source: enumRule(["linkedin", "indeed", "stepstone", "xing", "adzuna", "jooble", "remoteok", "arbeitnow", "other"]), resource_digest: digestRule } },
  ai_tailor_started: { fields: { ...baseAiFields, resource_digest: digestRule } },
  ai_tailor_completed: { fields: { ...baseAiFields, resource_digest: digestRule } },
  application_submitted: { fields: { method: enumRule(["manual", "auto", "email", "portal", "other"]), resource_digest: digestRule } },
  credit_used: { fields: { action: actionRule, cost: countRule, balance_after: countRule, source: enumRule(["pack", "subscription"]) } },
  credit_exhausted: { fields: { action_attempted: actionRule, tier: tierRule } },
  upgrade_clicked: { fields: { source: enumRule(["pricing_page", "low_credit_banner", "job_tracking_limit", "credit_exhaustion_modal"]), current_tier: tierRule, target_tier: tierRule } },
  subscription_started: { fields: { plan: tierRule, price_eur: { kind: "number", min: 0, max: 10_000 }, interval: enumRule(["monthly", "annual"]) } },
  subscription_cancelled: { fields: { plan: tierRule } },
  feature_used: { fields: { feature: actionRule, state: enumRule(["shown", "enabled", "disabled", "completed", "learn_more"]) } },
  session_started: { fields: { has_referrer: booleanRule, locale: localeRule } },
  page_viewed: { fields: { route: enumRule(["home", "pricing", "jobs", "job_detail", "resumes", "resume_detail", "applications", "blog", "auth", "settings", "other"]) } },
  ai_action_failed: { fields: { action: actionRule, error_type: errorTypeRule, error_code: errorCodeRule, duration_ms: durationRule, retries_exhausted: booleanRule } },
  page_error_shown: { fields: { error_type: errorTypeRule, route: enumRule(["home", "pricing", "jobs", "job_detail", "resumes", "resume_detail", "applications", "blog", "auth", "settings", "other"]) } },
  feedback_submitted: { fields: { type: enumRule(["bug", "feature", "general", "rating"]), rating: scoreRule, has_context: booleanRule } },
  campaign_landed: { fields: { source: enumRule(["organic", "paid", "email", "social", "partner", "other"]), landing_route: enumRule(["home", "pricing", "blog", "other"]) } },
  cta_clicked: { fields: { cta: enumRule(["signup", "pricing", "feature", "buy_pack"]), location: enumRule(["pricing_page", "low_credit_banner", "credit_exhaustion_modal", "blog", "other"]) } },
  credit_pack_purchased: { fields: { pack_type: enumRule(["starter", "boost", "power"]), credits: countRule } },
  referral_shared: { fields: { method: enumRule(["copy", "whatsapp", "email"]) } },
  referral_redeemed: { fields: { referee_tier: tierRule } },
  job_alert_created: { fields: { frequency: enumRule(["daily", "weekly"]), has_location: booleanRule, has_title_query: booleanRule } },
  blog_page_view: { fields: { pageType: enumRule(["country", "city", "role", "visa", "university", "editorial"]), locale: localeRule } },
  blog_cta_click: { fields: { cta_type: enumRule(["signup", "pricing", "feature"]), pageType: enumRule(["country", "city", "role", "visa", "university", "editorial"]) } },
  signup_from_blog: { fields: { source_pageType: enumRule(["country", "city", "role", "visa", "university", "editorial"]) } },
  blog_scroll_depth: { fields: { threshold: enumRule([25, 50, 75, 100]), pageType: enumRule(["country", "city", "role", "visa", "university", "editorial"]) } },
  blog_time_on_page: { fields: { threshold: enumRule([30, 60, 120]), pageType: enumRule(["country", "city", "role", "visa", "university", "editorial"]) } },
  account_deleted: { fields: {} },
  ai_generation_completed: { fields: { action: actionRule, duration_ms: durationRule, is_ai: booleanRule } },
  agent_conversation_started: { fields: { agent_name: agentRule, conversation_digest: digestRule, model_version: modelVersionRule, model_tier: modelTierRule, model_id: modelIdRule } },
  agent_conversation_completed: { fields: { agent_name: agentRule, conversation_digest: digestRule, tokens_used: countRule, prompt_tokens: countRule, completion_tokens: countRule, duration_ms: durationRule, model_version: modelVersionRule, model_tier: modelTierRule, model_id: modelIdRule } },
  agent_handoff: { fields: { from_agent: agentRule, to_agent: agentRule, conversation_digest: digestRule } },
  resume_tailor_completed: { fields: { conversation_digest: digestRule, model_version: modelVersionRule, model_id: modelIdRule, ats_score: scoreRule, critic_score: scoreRule, iterations: countRule, duration_ms: durationRule, success: booleanRule, convergence_exit: enumRule(["threshold_met", "max_iterations", "oscillation", "diminishing_returns", "unknown"]), score_history: { kind: "array", item: scoreRule, maxItems: 10 }, tier: tierRule, model_tier: modelTierRule } },
  ats_score_calculated: { fields: { conversation_digest: digestRule, agent_name: agentRule, model_version: modelVersionRule, model_id: modelIdRule, ats_score: scoreRule, iteration: countRule, duration_ms: durationRule, model_tier: modelTierRule } },
  swarm_started: { fields: { swarm_digest: digestRule, swarm_type: enumRule(["resume-tailoring", "career-coaching"]), step_count: countRule, model_version: modelVersionRule } },
  swarm_step_completed: { fields: { swarm_digest: digestRule, agent_name: agentRule, step_index: countRule, duration_ms: durationRule, tokens_used: countRule, model_version: modelVersionRule } },
  swarm_completed: { fields: { swarm_digest: digestRule, swarm_type: enumRule(["resume-tailoring", "career-coaching"]), total_tokens: countRule, total_duration_ms: durationRule, status: statusRule, model_version: modelVersionRule } },
  swarm_failed: { fields: { swarm_digest: digestRule, swarm_type: enumRule(["resume-tailoring", "career-coaching"]), failed_step: agentRule, error_code: errorCodeRule } },
  swarm_fallback: { fields: { swarm_type: enumRule(["resume-tailoring", "career-coaching"]), error_code: errorCodeRule } },
};

export function scrubEventProperties(event: unknown, properties: unknown): { event: string; properties: Record<string, unknown> } | undefined {
  if (typeof event !== "string") return undefined;
  const policy = OBSERVABILITY_EVENT_POLICIES[event];
  if (!policy) return undefined;
  return { event, properties: scrubObservability(properties, policy) };
}
